/**
 * OfflineSalesReportCard — "this device's own offline sales", built purely
 * from the local IndexedDB queue (lib/offlineDB.js). Works with zero
 * connectivity since it never calls the API — exactly what a cashier needs
 * to see while the terminal is cut off from the server.
 *
 * Once the queue syncs, rows move to the server-side Sales Summary report;
 * this card naturally empties out again.
 */
import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Smartphone, WifiOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { getAllOfflineSales } from '../../lib/offlineDB';
import { useOnlineStatus } from '../../lib/useOnlineStatus';
import { addSyncListener } from '../../lib/syncService';
import { fmtCurrency } from '../../lib/localeUtils';
import { keyed } from '../../lib/listKeys';

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
  synced: 'bg-emerald-100 text-emerald-700',
};

export const OfflineSalesReportCard = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const online = useOnlineStatus();

  const load = useCallback(() => {
    getAllOfflineSales().then(setSales).catch(() => setSales([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    return addSyncListener((event) => {
      if (event.includes('sales_sync') || event === 'full_sync_complete') load();
    });
  }, [load]);

  if (loading || sales.length === 0) return null;

  const totals = sales.reduce((acc, s) => {
    const items = (s.items || []).reduce((n, i) => n + (i.quantity || 0), 0);
    acc.revenue += s.total || 0;
    acc.items += items;
    return acc;
  }, { revenue: 0, items: 0 });

  return (
    <Card data-testid="offline-sales-report-card" className="border-amber-200 bg-amber-50/40 dark:bg-amber-950/10">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-amber-600" /> Offline sales (this device)
            </CardTitle>
            <CardDescription>Sales rung up on this terminal while offline. They'll appear in the normal Sales Summary once synced.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {!online && <Badge variant="outline" className="gap-1 text-xs text-amber-700 border-amber-300" data-testid="offline-sales-report-offline-badge"><WifiOff className="h-3 w-3" /> Working offline</Badge>}
            <Button size="sm" variant="outline" onClick={load} className="gap-1.5" data-testid="offline-sales-report-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-4 text-sm" data-testid="offline-sales-report-totals">
          <span><b>{sales.length}</b> sale{sales.length === 1 ? '' : 's'}</span>
          <span><b>{totals.items}</b> item{totals.items === 1 ? '' : 's'} sold</span>
          <span>Total: <b>{fmtCurrency(totals.revenue)}</b></span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keyed(sales, (s) => s.offline_id).map(({ item: s, key }) => (
                <TableRow key={key} data-testid={`offline-sales-report-row-${s.offline_id}`}>
                  <TableCell className="text-sm whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-sm">{s.customer_name || '—'}</TableCell>
                  <TableCell className="text-sm capitalize">{s.payment_method}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{fmtCurrency(s.total)}</TableCell>
                  <TableCell><Badge className={STATUS_BADGE[s.status] || STATUS_BADGE.pending}>{s.status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
